'use client'

import { IconButton, Icon, StyleProps } from '@/components/Chakra'
import useToast from '@/hooks/useToast'
import { ESJob, Job } from '@/types/job'
import { useContext, useEffect, useState, useTransition } from 'react'
import { HiHeart } from 'react-icons/hi'
import { jobFavoriteAction, jobUnFavoriteAction } from '@/features/jobFavorites/action'
import { useServerActionHandler } from '@/hooks/useServerActionHandler'
import { AuthContext } from '@/hooks/useAuthContext'
import useUrl from '@/hooks/useUrl'
import { useRouter } from 'next/navigation'

type SchemeKey = 'default' | 'transparent'

type Props = {
  job: ESJob | Job
  scheme?: SchemeKey
  successCallback?: (job: ESJob | Job) => void
} & StyleProps

type ColorType = {
  bgColor: Color
  hoverBgColor: Color
  iconColor: Color
  borderColor: string
}

type Color = {
  on: string
  off: string
}

export const FavoriteIconButton = ({ job, successCallback, scheme = 'default', ...props }: Props) => {
  const [isPending, startTransition] = useTransition()
  const [isFavorite, setIsFavorite] = useState(job.is_favorited)
  const toast = useToast()
  const serverActionHandler = useServerActionHandler()
  const authCtx = useContext(AuthContext)
  const { currentUrl, host } = useUrl()
  const router = useRouter()

  const colors: { [key in SchemeKey]: ColorType } = {
    default: {
      bgColor: {
        on: 'primary.100',
        off: 'white',
      },
      hoverBgColor: {
        on: 'primary.200',
        off: 'gray.200',
      },
      iconColor: {
        on: 'primary.500',
        off: 'gray.300',
      },
      borderColor: 'gray.200',
    },
    transparent: {
      bgColor: {
        on: 'primary.100',
        off: 'transparent',
      },
      hoverBgColor: {
        on: 'primary.200',
        off: 'gray.200',
      },
      iconColor: {
        on: 'primary.500',
        off: 'white',
      },
      borderColor: 'white',
    },
  }

  useEffect(() => {
    setIsFavorite(job.is_favorited)
  }, [job.is_favorited])

  const handleFavoriteAction = (job: ESJob | Job) => {
    if (!authCtx.isLoggedIn) {
      const current = currentUrl.replace(host, '')
      const redirectUrl = `/signin?redirect_to=${current}`
      router.push(redirectUrl)
      return
    }

    startTransition(() => {
      const action = job.is_favorited ? jobUnFavoriteAction : jobFavoriteAction
      action(job.id)
        .then(serverActionHandler.responseHandler)
        .then(() => {
          setIsFavorite(!job.is_favorited)
          job.is_favorited = !job.is_favorited
          toast({
            title: job.is_favorited
              ? `「${job.name}」をお気に入りに追加しました。`
              : `「${job.name}」をお気に入りから解除しました。`,
          })
          if (successCallback) successCallback(job)
        })
        .catch(serverActionHandler.errorHandler)
    })
  }

  return (
    <>
      <IconButton
        isRound={true}
        variant={'solid'}
        bg={'transparent'}
        border={'1px solid'}
        borderColor={colors[scheme].borderColor}
        aria-label={''}
        bgColor={isFavorite ? colors[scheme].bgColor.on : colors[scheme].bgColor.off}
        _hover={{ bg: isFavorite ? colors[scheme].hoverBgColor.on : colors[scheme].hoverBgColor.off }}
        isLoading={isPending}
        onClick={(e) => {
          e.preventDefault()
          handleFavoriteAction(job)
        }}
        isDisabled={isPending}
        icon={
          <Icon
            verticalAlign={'middle'}
            fontSize={'xl'}
            color={isFavorite ? colors[scheme].iconColor.on : colors[scheme].iconColor.off}
            as={HiHeart}
          />
        }
        {...props}
      />
    </>
  )
}
